import { useState, useMemo, useEffect, type KeyboardEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUserStore } from '../store/userStore';
import { createCheckoutSession } from '../services/api';
import { getAuth } from 'firebase/auth';

interface PricingTier {
  name: string;
  price: string;
  period: string;
  description: string;
  features: string[];
  cta: string;
  popular?: boolean;
}

interface PricingDetails {
  monthlyPrice: number;
  yearlyPrice: number;
  yearlySavings: number;
  yearlyMonthlyEquivalent: number;
}

type TierKey = 'Free' | 'Standard' | 'Premium';

interface ComparisonCell {
  included?: boolean;
  text?: string;
}

interface ComparisonRow {
  feature: string;
  helper?: string;
  tiers: Record<TierKey, ComparisonCell>;
}

const pricingDetails: Record<string, PricingDetails> = {
  standard: {
    monthlyPrice: 9.99,
    yearlyPrice: 99,
    yearlySavings: 20.88,
    yearlyMonthlyEquivalent: 8.25,
  },
  premium: {
    monthlyPrice: 19.99,
    yearlyPrice: 199,
    yearlySavings: 40.88,
    yearlyMonthlyEquivalent: 16.58,
  },
};

const comparisonRows: ComparisonRow[] = [
  {
    feature: 'Conversation sessions',
    helper: 'Length and AI model used for speaking practice',
    tiers: {
      Free: { text: 'Short, daily limit' },
      Standard: { text: 'Unlimited • Gemini AI' },
      Premium: { text: 'Unlimited • Claude AI' },
    },
  },
  {
    feature: 'Flashcards & spaced repetition',
    helper: 'Save phrases and review them whenever you want',
    tiers: {
      Free: { included: true },
      Standard: { included: true },
      Premium: { included: true },
    },
  },
  {
    feature: 'Advanced analytics dashboard',
    helper: 'XP breakdowns, streaks, and personalized recommendations',
    tiers: {
      Free: { included: false },
      Standard: { included: true },
      Premium: { included: true },
    },
  },
  {
    feature: 'Premium AI voices',
    helper: 'Immersive ElevenLabs voices + custom selections',
    tiers: {
      Free: { included: false },
      Standard: { included: false },
      Premium: { included: true },
    },
  },
  {
    feature: 'Priority support',
    helper: 'Fast-lane access to our product team',
    tiers: {
      Free: { included: false },
      Standard: { included: false },
      Premium: { included: true },
    },
  },
];

const tierOrder: TierKey[] = ['Free', 'Standard', 'Premium'];

const CheckIcon = () => (
  <svg className="h-5 w-5 text-green-500" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
    <path
      fillRule="evenodd"
      d="M16.707 5.293a1 1 0 010 1.414l-7.25 7.25a1 1 0 01-1.414 0L3.293 9.207a1 1 0 111.414-1.414l4.043 4.043 6.543-6.543a1 1 0 011.414 0z"
      clipRule="evenodd"
    />
  </svg>
);

const DashIcon = () => (
  <svg className="h-5 w-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
    <path fillRule="evenodd" d="M4 9h12v2H4z" clipRule="evenodd" />
  </svg>
);

const getTiers = (interval: 'monthly' | 'yearly'): PricingTier[] => [
  {
    name: 'Free',
    price: '$0',
    period: 'forever',
    description: 'Perfect for getting started with Japanese',
    features: [
      'Google Text to Speech',
      'Limited conversation session length',
      'Limited flashcards',
      'Progression tracking',
    ],
    cta: 'Current Plan',
  },
  {
    name: 'Standard',
    price: interval === 'monthly'
      ? `$${pricingDetails.standard.monthlyPrice}`
      : `$${pricingDetails.standard.yearlyPrice}`,
    period: interval === 'monthly' ? 'per month' : 'per year',
    description: 'Enhanced AI learning at an affordable price',
    features: [
      'Google Text to Speech',
      'Gemini AI for conversations',
      'Unlimited Conversation Sessions',
      'Unlimited Flashcards',
      'Advanced Analytics',
    ],
    cta: 'Upgrade to Standard',
    popular: false,
  },
  {
    name: 'Premium',
    price: interval === 'monthly'
      ? `$${pricingDetails.premium.monthlyPrice}`
      : `$${pricingDetails.premium.yearlyPrice}`,
    period: interval === 'monthly' ? 'per month' : 'per year',
    description: 'Unlock the full power of AI-driven learning',
    features: [
      'ElevenLabs AI Voices',
      'Claude AI for conversations',
      'Custom Voice Selection',
      'Unlimited Conversation Sessions',
      'Unlimited Flashcards',
      'Advanced Analytics',
    ],
    cta: 'Upgrade to Premium',
    popular: true,
  },
];

export function Pricing() {
  const navigate = useNavigate();
  const { user } = useUserStore();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [billingInterval, setBillingInterval] = useState<'monthly' | 'yearly'>('monthly');
  const tiers = useMemo(() => getTiers(billingInterval), [billingInterval]);
  const [mobileActiveTier, setMobileActiveTier] = useState<string>(tiers[0]?.name ?? 'Free');

  useEffect(() => {
    setMobileActiveTier(tiers[0]?.name ?? 'Free');
  }, [billingInterval, tiers]);

  const handleUpgrade = async (tier: 'standard' | 'premium') => {
    setLoading(true);
    setError(null);

    try {
      // Check if user is authenticated
      const auth = getAuth();
      const currentUser = auth.currentUser;

      if (!currentUser) {
        setError(`Please sign in to upgrade to ${tier === 'standard' ? 'Standard' : 'Premium'}`);
        setLoading(false);
        setTimeout(() => navigate('/auth'), 2000);
        return;
      }

      // Verify we have the user in our store
      if (!user) {
        setError('User data not found. Please refresh and try again.');
        setLoading(false);
        return;
      }

      // Create Stripe checkout session
      const baseUrl = window.location.origin;
      const { url } = await createCheckoutSession(
        `${baseUrl}/profile?success=true`,
        `${baseUrl}/pricing?canceled=true`,
        tier,
        billingInterval
      );

      // Redirect to Stripe checkout
      window.location.href = url;
    } catch (err: any) {
      console.error('Failed to create checkout session:', err);
      const errorMessage = err?.message || 'Failed to start checkout. Please try again.';
      setError(errorMessage);
      setLoading(false);
    }
  };

  const handleCTA = (tier: PricingTier) => {
    if (tier.name === 'Free') {
      if (user?.subscriptionTier === 'free') {
        navigate('/profile');
      } else {
        // They have a paid plan, let them manage in profile
        navigate('/profile');
      }
    } else if (tier.name === 'Standard') {
      if (user?.subscriptionTier === 'standard') {
        navigate('/profile');
      } else {
        handleUpgrade('standard');
      }
    } else {
      // Premium tier
      if (user?.subscriptionTier === 'premium') {
        navigate('/profile');
      } else {
        handleUpgrade('premium');
      }
    }
  };

  const getButtonText = (tier: PricingTier) => {
    if (!user) return tier.cta;

    if (tier.name === 'Free') {
      return user.subscriptionTier === 'free' ? 'Current Plan' : 'Manage Plan';
    } else if (tier.name === 'Standard') {
      return user.subscriptionTier === 'standard' ? 'Current Plan' : 'Upgrade to Standard';
    } else {
      return user.subscriptionTier === 'premium' ? 'Current Plan' : 'Upgrade to Premium';
    }
  };

  const renderTierCard = (tier: PricingTier, { isMobile = false }: { isMobile?: boolean } = {}) => {
    const isDisabled =
      loading ||
      (user?.subscriptionTier === 'free' && tier.name === 'Free') ||
      (user?.subscriptionTier === 'standard' && tier.name === 'Standard') ||
      (user?.subscriptionTier === 'premium' && tier.name === 'Premium');

    const handleTierSelect = () => {
      if (isDisabled) return;
      handleCTA(tier);
    };

    const handleCardKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        handleTierSelect();
      }
    };

    const cardClasses = `relative bg-[var(--color-surface)] rounded-xl shadow-lg overflow-hidden transition-all duration-200 ${
      isDisabled ? 'cursor-not-allowed' : 'hover:scale-[1.02] hover:shadow-xl cursor-pointer'
    } ${tier.popular ? 'ring-2 ring-primary-500' : 'border border-[var(--color-border)]'}`;

    return (
      <div
        key={`${tier.name}-${isMobile ? 'mobile' : 'desktop'}`}
        role="button"
        tabIndex={0}
        onClick={handleTierSelect}
        onKeyDown={handleCardKeyDown}
        className={cardClasses}
        aria-disabled={isDisabled}
      >
        {tier.popular && (
          <div className="absolute top-0 right-0 bg-primary-500 text-white px-4 py-1 text-sm font-semibold rounded-bl-lg">
            Most Popular
          </div>
        )}

        <div className="p-6 sm:p-8">
          <h2 className="text-2xl font-semibold text-[var(--color-text)] mb-2">{tier.name}</h2>
          <p className="text-[var(--color-text-secondary)] mb-4 sm:mb-6">{tier.description}</p>

          <div className="mb-5 sm:mb-6">
            <span className="text-4xl sm:text-5xl font-semibold text-[var(--color-text)]">{tier.price}</span>
            <span className="text-[var(--color-text-secondary)] ml-2">/{tier.period}</span>

            {billingInterval === 'yearly' && tier.name !== 'Free' && pricingDetails[tier.name.toLowerCase()] && (
              <div className="mt-2">
                <span className="text-sm text-success-600 font-semibold">
                  Save ${pricingDetails[tier.name.toLowerCase()].yearlySavings.toFixed(2)}/year
                </span>
                <span className="text-xs text-[var(--color-muted)] block">
                  vs. monthly billing (${pricingDetails[tier.name.toLowerCase()].yearlyMonthlyEquivalent.toFixed(2)}/month)
                </span>
              </div>
            )}
          </div>

          <button
            onClick={() => handleCTA(tier)}
            disabled={isDisabled}
            className={`w-full py-3 px-6 rounded-xl font-semibold text-lg transition-all duration-200 mb-4 sm:mb-6 ${
              tier.popular
                ? 'bg-primary-500 text-white hover:bg-primary-600 disabled:bg-[var(--color-border)]'
                : 'bg-[var(--color-surface-secondary)] text-[var(--color-text)] hover:bg-[var(--color-border)] disabled:bg-[var(--color-surface-secondary)]'
            } disabled:cursor-not-allowed`}
          >
            {loading && (tier.name === 'Standard' || tier.name === 'Premium') ? 'Loading...' : getButtonText(tier)}
          </button>

          <div className="space-y-3">
            <p className="text-sm font-semibold text-[var(--color-text)] uppercase tracking-wide">
              What&apos;s included:
            </p>
            {isMobile ? (
              <details open className="rounded-xl border border-[var(--color-border)] bg-[var(--color-surface-secondary)] p-4 text-left">
                <summary className="text-sm font-medium text-[var(--color-text-secondary)] cursor-pointer">What's included</summary>
                <div className="mt-3 space-y-2">
                  {tier.features.map((feature, index) => (
                    <div key={index} className="flex items-start">
                      <svg
                        className="w-5 h-5 text-success-500 mr-3 flex-shrink-0 mt-0.5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span className="text-[var(--color-text-secondary)] text-sm">{feature}</span>
                    </div>
                  ))}
                </div>
              </details>
            ) : (
              tier.features.map((feature, index) => (
                <div key={index} className="flex items-start">
                  <svg
                    className="w-5 h-5 text-success-500 mr-3 flex-shrink-0 mt-0.5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="text-[var(--color-text-secondary)]">{feature}</span>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="page-shell py-12 px-4 bg-[var(--color-bg)]">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-semibold text-[var(--color-text)] mb-4">
            Choose Your Learning Journey
          </h1>
          <p className="text-xl text-[var(--color-text-secondary)] max-w-2xl mx-auto">
            Start for free, upgrade when you're ready to unlock premium AI voices and unlimited practice
          </p>

          {/* Billing Toggle */}
          <div className="flex items-center justify-center mt-8 gap-4">
            <span className={`text-sm font-medium transition-colors duration-200 ${billingInterval === 'monthly' ? 'text-[var(--color-text)]' : 'text-[var(--color-muted)]'}`}>
              Monthly
            </span>
            <button
              onClick={() => setBillingInterval(billingInterval === 'monthly' ? 'yearly' : 'monthly')}
              className="relative inline-flex h-6 w-11 items-center rounded-full bg-primary-500 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-200 ${
                  billingInterval === 'yearly' ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
            <div className="flex items-center gap-2">
              <span className={`text-sm font-medium transition-colors duration-200 ${billingInterval === 'yearly' ? 'text-[var(--color-text)]' : 'text-[var(--color-muted)]'}`}>
                Yearly
              </span>
              <span className="inline-flex items-center rounded-full bg-success-100 px-3 py-1 text-xs font-semibold text-success-700">
                Save 17%
              </span>
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="max-w-2xl mx-auto mb-6 bg-error-50 border border-error-200 rounded-xl p-4">
            <p className="text-error-700 text-center">{error}</p>
          </div>
        )}

        {/* Pricing Cards */}
        <div className="md:hidden max-w-3xl mx-auto">
          <div className="grid grid-cols-3 gap-3 mb-5">
            {tiers.map((tier) => (
              <button
                key={`${tier.name}-selector`}
                type="button"
                onClick={() => setMobileActiveTier(tier.name)}
                className={`rounded-2xl border px-3 py-3 text-left transition shadow-sm ${
                  mobileActiveTier === tier.name
                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                    : 'border-gray-200 bg-white text-gray-700'
                }`}
              >
                <p className="text-xs font-semibold uppercase tracking-wide">{tier.name}</p>
                <p className="text-sm font-bold">{tier.price}</p>
                <p className="text-[11px] text-gray-500">/ {tier.period}</p>
              </button>
            ))}
          </div>
          {tiers
            .filter((tier) => tier.name === mobileActiveTier)
            .map((tier) => (
              <div key={`${tier.name}-mobile`}>{renderTierCard(tier, { isMobile: true })}</div>
            ))}
        </div>

        <div className="hidden md:grid md:grid-cols-3 gap-6 max-w-6xl mx-auto">
          {tiers.map((tier) => renderTierCard(tier))}
        </div>

        {/* Comparison Section */}
        <div className="mt-16">
          <div className="text-center mb-10">
            <p className="text-sm font-semibold uppercase tracking-wide text-primary-500">Feature breakdown</p>
            <h2 className="text-3xl font-semibold text-[var(--color-text)] mt-2">Compare plans at a glance</h2>
          </div>
          {/* Desktop Table */}
          <div className="hidden md:block">
            <div className="overflow-hidden rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] shadow-md">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                      Feature
                    </th>
                    {tierOrder.map((tier) => (
                      <th key={tier} scope="col" className="px-6 py-4 text-center text-xs font-semibold uppercase tracking-wider text-gray-500">
                        {tier}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {comparisonRows.map((row, rowIdx) => (
                    <tr key={row.feature} className={rowIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}>
                      <td className="px-6 py-5">
                        <p className="font-semibold text-gray-900">{row.feature}</p>
                        {row.helper && <p className="text-sm text-gray-500 mt-1">{row.helper}</p>}
                      </td>
                      {tierOrder.map((tier) => {
                        const value = row.tiers[tier];
                        const hasCheck = typeof value?.included === 'boolean';
                        return (
                          <td key={`${row.feature}-${tier}`} className="px-6 py-5 text-center">
                            <div className="flex flex-col items-center justify-center gap-2 text-sm text-gray-900">
                              {hasCheck &&
                                (value?.included ? (
                                  <CheckIcon />
                                ) : (
                                  <DashIcon />
                                ))}
                              {value?.text && (
                                <span className="text-sm text-gray-700">{value.text}</span>
                              )}
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Mobile stacked cards */}
          <div className="md:hidden space-y-3">
            {comparisonRows.map((row) => (
              <div key={`${row.feature}-mobile`} className="bg-white border border-gray-200 rounded-2xl p-4 shadow-sm">
                <p className="text-base font-semibold text-gray-900">{row.feature}</p>
                {row.helper && <p className="text-sm text-gray-500 mt-1">{row.helper}</p>}
                <div className="mt-3 grid grid-cols-3 gap-3 text-center text-xs">
                  {tierOrder.map((tier) => {
                    const value = row.tiers[tier];
                    const hasCheck = typeof value?.included === 'boolean';
                    return (
                      <div key={`${row.feature}-${tier}-mobile`} className="flex flex-col items-center gap-1">
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">{tier}</p>
                        {hasCheck &&
                          (value?.included ? (
                            <CheckIcon />
                          ) : (
                            <DashIcon />
                          ))}
                        {value?.text && <p className="text-xs text-gray-800">{value.text}</p>}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* FAQ Section */}
        <div className="mt-16 max-w-3xl mx-auto">
          <h2 className="text-3xl font-semibold text-center text-[var(--color-text)] mb-8">
            Frequently Asked Questions
          </h2>
          <div className="space-y-6">
            <div className="bg-[var(--color-surface)] rounded-xl p-6 shadow-md border border-[var(--color-border)]">
              <h3 className="text-lg font-semibold text-[var(--color-text)] mb-2">
                Can I cancel anytime?
              </h3>
              <p className="text-[var(--color-text-secondary)]">
                Yes! You can cancel your premium subscription at any time. You'll
                continue to have access until the end of your billing period.
              </p>
            </div>
            <div className="bg-[var(--color-surface)] rounded-xl p-6 shadow-md border border-[var(--color-border)]">
              <h3 className="text-lg font-semibold text-[var(--color-text)] mb-2">
                What payment methods do you accept?
              </h3>
              <p className="text-[var(--color-text-secondary)]">
                We accept all major credit and debit cards through Stripe, our
                secure payment processor.
              </p>
            </div>
            <div className="bg-[var(--color-surface)] rounded-xl p-6 shadow-md border border-[var(--color-border)]">
              <h3 className="text-lg font-semibold text-[var(--color-text)] mb-2">
                What are ElevenLabs AI Voices?
              </h3>
              <p className="text-[var(--color-text-secondary)]">
                ElevenLabs provides premium, natural-sounding AI voices that sound
                incredibly realistic. This makes your Japanese conversation practice
                feel more authentic and engaging compared to standard text-to-speech.
              </p>
            </div>
          </div>
        </div>

        {/* Back Button */}
        <div className="mt-12 text-center">
          <button
            onClick={() => navigate('/')}
            className="text-primary-500 hover:text-primary-600 font-medium transition-colors duration-200"
          >
            ← Back to Home
          </button>
        </div>
      </div>
    </div>
  );
}
